/* ============================================================================
   jd-jewelry-row — behaviour for sections/jd-jewelry-row.liquid
   ----------------------------------------------------------------------------
   Four jobs: measure the full-bleed width, drive the arrow buttons, drag-to-scroll
   with a mouse, and load the next page of products when the track is scrolled to
   its end.

   PER-INSTANCE, unlike assets/jd-jewelry-grid.js. That file takes the FIRST
   .jjg-wrap on the page and wires only that one; a row is a component that can
   legitimately appear several times on a homepage, so every .jjr-wrap is
   initialised and each is guarded by data-jjr-init. Two row sections mean two
   identical <script src> tags, which the browser executes twice — the guard is
   what makes the second run a no-op on the row the first run already claimed.

   Everything it needs from Liquid is read off .jjr-wrap: data-jjr-sid,
   data-jjr-bleed, data-jjr-drag.
   ============================================================================ */
(function () {
  var wraps = document.querySelectorAll('.jjr-wrap');
  if (!wraps.length) return;

  /* ── WISHLIST ──────────────────────────────────────
     Deliberately duplicated from assets/jd-jewelry-grid.js rather than shared.
     That file is one IIFE with no exports, loaded un-deferred at a position its
     own header documents as load-bearing; carving an export out of it to save
     twenty lines here would be a change to live grid behaviour on 17 templates.
     The storage key and the entry shape are the contract — keep them in step.
     Both files scope their queries to their own wrapper, so a page with a grid
     and a row initialises each button exactly once. */
  var WL_KEY = 'jd_wishlist';
  function wlGetList() { try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); } catch (e) { return []; } }
  function wlSave(l) { localStorage.setItem(WL_KEY, JSON.stringify(l)); }
  function wlMatchId(e, id) { return typeof e === 'string' ? e === id : e && e.id === id; }
  function wlToggle(id, handle) {
    var l = wlGetList();
    var idx = l.findIndex(function (e) { return wlMatchId(e, id); });
    if (idx === -1) { l.push({ id: id, handle: handle }); wlSave(l); return true; }
    l.splice(idx, 1); wlSave(l); return false;
  }

  function initWishlistBtn(btn) {
    if (btn.dataset.wishlistInit) return;
    btn.dataset.wishlistInit = '1';
    if (wlGetList().some(function (e) { return wlMatchId(e, btn.dataset.productId); })) btn.classList.add('active');
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      var pid = btn.dataset.productId;
      var added = wlToggle(pid, btn.dataset.productHandle);
      // Every button for this product on the page, so a grid and a row stay in sync.
      document.querySelectorAll('[data-product-id="' + pid + '"][class*="wishlist-btn"]').forEach(function (b) {
        b.classList.toggle('active', added);
        if (b.className.indexOf('ai-wishlist-btn-') !== -1) b.textContent = added ? '♥' : '♡';
      });
      btn.classList.remove('just-toggled');
      void btn.offsetWidth;
      btn.classList.add('just-toggled');
      btn.addEventListener('animationend', function () { btn.classList.remove('just-toggled'); }, { once: true });
    });
  }

  /* ── FULL BLEED ────────────────────────────────────
     100vw includes the classic desktop scrollbar, so a row sized by it overflows
     the page by ~15px and gives the document its own horizontal scrollbar.
     documentElement.clientWidth is the viewport minus that scrollbar. Written to
     every wrap (not :root) so it survives the theme editor re-rendering one
     section without a resize event. */
  function measureBleed() {
    var vw = document.documentElement.clientWidth + 'px';
    document.querySelectorAll('.jjr-wrap[data-jjr-bleed="true"]').forEach(function (w) {
      w.style.setProperty('--jjr-vw', vw);
    });
  }

  function init(wrap) {
    if (wrap.dataset.jjrInit) return;
    wrap.dataset.jjrInit = '1';

    var sid   = wrap.dataset.jjrSid || '';
    var track = wrap.querySelector('.jjr-track');
    if (!track) return;
    var prevBtn = wrap.querySelector('.jjr-nav.prev');
    var nextBtn = wrap.querySelector('.jjr-nav.next');

    /* ══ ENDLESS LOOP ═════════════════════════════════
       Wrapping in both directions is done the way every infinite carousel does it:
       copies of the cards are placed on both sides of the real set, and when the
       scroll position walks past the real set it is teleported back by exactly one
       set width. Because the content one set away is pixel-identical, the jump is
       invisible — the row simply never reaches an end.

       Two things this is NOT:
       · Not a clone of the WHOLE set on each side. Only enough cards to cover one
         viewport plus one are cloned (cycling the list if the collection is shorter
         than a viewport), so a 24-product row costs ~12 extra nodes, not 48.
       · Not compatible with the "load more" pagination. A set that is still growing
         moves the seam under the user, so the section stops rendering the sentinel
         when the loop is on and the products it did render are what circulate.

       The known cost: writing scrollLeft during an iOS momentum fling stops the
       fling. The teleport only happens after a full set has gone by, so it is rare,
       and it lands on identical content — but it is why the buffer is sized to a
       whole viewport rather than a couple of cards. */
    var loop = null;   // assigned below, once the helpers it needs are defined

    function buildLoop() {
      if (wrap.dataset.jjrLoop !== 'true') return null;
      var originals = Array.prototype.slice.call(track.querySelectorAll('.jjg-item'));
      var n = originals.length;
      if (n < 2) return null;   // nothing to wrap around

      function cloneCard(src) {
        var c = src.cloneNode(true);
        c.classList.add('jjr-clone');
        // A duplicate must not be announced or tabbed through. Every focusable
        // descendant is neutralised too, otherwise aria-hidden would be hiding
        // focusable content — an a11y error in its own right.
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('tabindex', '-1');
        c.querySelectorAll('button, a, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        // The snippet marks the first four cards eager, and card 1 fetchpriority=high,
        // because they paint above the fold. A copy of them never does — cloning those
        // hints unchanged would put a duplicate of the hero image at the front of the
        // network queue, competing with the original it is a copy of.
        c.querySelectorAll('img').forEach(function (im) {
          im.setAttribute('loading', 'lazy');
          im.setAttribute('decoding', 'async');
          im.removeAttribute('fetchpriority');
        });
        return c;
      }

      var state = { originals: originals, marker: null, built: 0, period: 0, home: 0 };

      /* The buffer is measured in CARDS, not sets: `built` is how many cards of the
         set exist on each side. Extending it never touches what is already there, so
         a widened window costs a few nodes rather than a rebuild. The modulo lets the
         buffer be longer than the collection itself, which is what makes a three-
         product row loop as happily as a fifty-product one. */
      state.grow = function () {
        var gap  = parseFloat(getComputedStyle(track).columnGap) || 0;
        var unit = originals[0].getBoundingClientRect().width + gap;
        if (unit <= 0) return;
        /* TWO viewports of cards, plus one so a partly-visible card is never missing.
           One viewport is what has to be RENDERED when the position sits at the far
           end of the valid window; the second is headroom for an arrow press to
           animate into before it is wrapped back. Sizing this to one viewport looked
           right and was not: from the end of the window a press asked for another
           ~1.2 screens, the browser clamped the scroll at its physical maximum, and
           the row visibly stopped dead every fourth press instead of wrapping. */
        var want = Math.ceil(2 * track.clientWidth / unit) + 1;
        if (want <= state.built) return;

        // Trailing: the set continuing past its own end — card 0, 1, 2 …
        var after = document.createDocumentFragment();
        for (var i = state.built; i < want; i++) {
          var ca = cloneCard(originals[i % n]);
          if (i === 0) state.marker = ca;   // card 0's own clone: one period along
          after.appendChild(ca);
        }
        track.appendChild(after);

        // Leading: the set running backwards from its own start, built far-end first
        // so the DOM order stays … n-2, n-1, [real card 0].
        var before = document.createDocumentFragment();
        for (var j = want; j > state.built; j--) {
          before.appendChild(cloneCard(originals[((n - j) % n + n) % n]));
        }
        track.insertBefore(before, track.firstElementChild);

        state.built = want;
      };

      /* Both landmarks are read off live layout rather than calculated, so fractional
         card widths, the gap and the track's edge padding can never drift out of step
         with them.
           period — the distance from a card to its own clone one set later.
           home   — the scrollLeft that puts the real card 0 exactly where the track's
                    first child sits at scrollLeft 0, i.e. at the edge inset. */
      state.measure = function () {
        state.period = state.marker.offsetLeft - originals[0].offsetLeft;
        state.home   = originals[0].offsetLeft - track.firstElementChild.offsetLeft;
      };

      state.grow();
      if (!state.marker) return null;
      state.measure();
      if (state.period <= 0) return null;

      return state;
    }

    // Set by the drag handler. A drag holds the scroll position it started from and
    // works in deltas off it, so a teleport underneath has to move that origin by the
    // same amount or the next pointermove would yank the row straight back.
    var onJump = null;

    // Teleports are instant by definition. The stylesheet puts scroll-behavior:smooth
    // on the track, and that applies to a scrollLeft assignment too — without this the
    // "invisible" jump would animate across a whole set.
    function jumpTo(x) {
      var delta = x - track.scrollLeft;
      var prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = x;
      track.style.scrollBehavior = prev;
      if (onJump) onJump(delta);
    }

    /* Arrow presses animate, and an animation that crosses the seam would be killed
       by the teleport. step() jumps BEFORE it animates so the smooth scroll always
       runs inside the buffer, and normalising is held off until it has landed. */
    var holdUntil = 0;

    function normalize(force) {
      if (!loop) return;
      // The hold exists to protect a running animation; an arrow press starting a
      // NEW one has to be able to re-home first, hence force.
      if (!force && performance.now() < holdUntil) return;
      var x = track.scrollLeft;
      if (x >= loop.home + loop.period) jumpTo(x - loop.period);
      else if (x < loop.home)           jumpTo(x + loop.period);
    }

    /* Build before anything else touches the cards: cloneNode copies the
       data-wishlist-init guard along with everything else, so the clones have to
       exist BEFORE the wishlist pass or their hearts would be dead. Building is
       synchronous and this script is not deferred, so the row is already parked on
       the real card 0 by the first paint — the leading clones never flash into view. */
    loop = buildLoop();
    if (loop) {
      wrap.classList.add('jjr-loop');
      jumpTo(loop.home);
      // The markup ships "prev" disabled because a non-looping row starts at its
      // left end. A looping one has no ends, and sync() skips the arrow state
      // entirely from here on, so nothing else would ever clear it.
      if (prevBtn) prevBtn.disabled = false;
      if (nextBtn) nextBtn.disabled = false;
    }

    wrap.querySelectorAll('.pc-wishlist-btn').forEach(initWishlistBtn);

    /* ── ARROW STATE ─────────────────────────────────
       A 2px tolerance, not 0: scrollLeft is fractional at fractional card widths
       and at the end it lands a hair short of scrollWidth - clientWidth, which
       would leave the "next" arrow enabled with nowhere left to go. A looping row
       has no ends, so neither arrow is ever disabled. */
    var raf = null;
    function sync() {
      raf = null;
      normalize();
      if (loop) {
        // Sampled here, on every scroll, because by the time a resize is observed the
        // browser has ALREADY rescaled scrollLeft to the new content width — reading
        // the position inside the resize handler measures the aftermath, not where the
        // row was. This is the last value from before it.
        if (loop.period > 0) loop.frac = (track.scrollLeft - loop.home) / loop.period;
        return;
      }
      var max = track.scrollWidth - track.clientWidth;
      wrap.classList.toggle('jjr-static', max <= 2);
      if (prevBtn) prevBtn.disabled = track.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 2;
    }
    function syncSoon() { if (raf === null) raf = requestAnimationFrame(sync); }

    track.addEventListener('scroll', syncSoon, { passive: true });

    /* ── ARROWS ──────────────────────────────────────
       One press moves by whole cards — as many as fit in view — so the row always
       comes to rest on a card boundary whatever the snap setting is. Reading the
       gap and the card width from live layout means the fractional "cards in view"
       setting needs no separate maths here. */
    function step(dir) {
      var card = track.querySelector('.jjg-item');
      var gap  = parseFloat(getComputedStyle(track).columnGap) || 0;
      var unit = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
      var n    = Math.max(1, Math.floor(track.clientWidth / unit));
      var move = dir * unit * n;

      if (loop) {
        // Land inside the real set first, so the animation that follows stays in the
        // buffer and is never cut short by a teleport, then hold normalising off
        // until it has finished. The buffer is a full viewport wide, which is the
        // most one press can travel, so the overshoot always has somewhere to go.
        normalize(true);
        holdUntil = performance.now() + 800;
      }

      track.scrollBy({ left: move, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });

    /* ── DRAG (mouse only) ───────────────────────────
       Touch is left to the browser: native scrolling already has momentum and
       rubber-banding, and a pointermove handler there fights the compositor.
       Pointer capture keeps the drag alive when the cursor leaves the track. */
    if (wrap.dataset.jjrDrag === 'true') {
      var down = false, moved = false, startX = 0, startLeft = 0, pid = null;

      // See the declaration of onJump: keep this drag's origin in step with a wrap.
      onJump = function (delta) { startLeft += delta; };

      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        // The wishlist heart and the arrows own their own clicks.
        if (e.target.closest('.pc-wishlist-btn, .jjr-nav')) return;
        down = true; moved = false;
        startX = e.clientX; startLeft = track.scrollLeft; pid = e.pointerId;
      });

      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        // 5px of slack so a slightly shaky click is still a click.
        if (!moved) {
          if (Math.abs(dx) < 5) return;
          moved = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(pid); } catch (err) { /* capture is a nicety */ }
        }
        track.scrollLeft = startLeft - dx;
      });

      function endDrag() {
        if (!down) return;
        down = false;
        track.classList.remove('is-dragging');
        if (pid !== null) { try { track.releasePointerCapture(pid); } catch (err) {} }
        pid = null;
        // `moved` is NOT reset here: the click that follows this pointerup has to
        // be able to see it. The next pointerdown clears it.
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      // Capture phase — the card is an <a>, so this has to run before the link does.
      track.addEventListener('click', function (e) {
        if (!moved) return;
        e.preventDefault();
        e.stopPropagation();
      }, true);

      // Without this the browser starts a native link drag instead of scrolling.
      track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }

    /* ── LOAD MORE ───────────────────────────────────
       Same fetch-and-parse as the grid's infinite scroll, but the observer's root
       is the track and the margin is on the RIGHT edge, so it fires on horizontal
       scroll. The sentinel stays put at the end of the track and the new cards are
       inserted before it. */
    var sentinel = track.querySelector('.jjr-sentinel');
    // A looping row has no end to trigger on, and the section stops rendering the
    // sentinel when the loop is on. This only catches a stale one (a cached page from
    // before the setting was switched), which would otherwise sit inside the clones.
    if (loop && sentinel) { sentinel.remove(); sentinel = null; }
    if (sentinel && sentinel.dataset.next) {
      var loading = false;
      var observer = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting || loading) return;
        loading = true;

        fetch(sentinel.dataset.next)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newTrack = doc.getElementById('jjr-track-' + sid);
            if (newTrack) {
              newTrack.querySelectorAll('.jjg-item').forEach(function (el) {
                track.insertBefore(el, sentinel);   // insertBefore adopts across documents
                el.querySelectorAll('.pc-wishlist-btn').forEach(initWishlistBtn);
              });
            }
            var newSentinel = doc.getElementById('jjr-sentinel-' + sid);
            if (newSentinel && newSentinel.dataset.next) {
              sentinel.dataset.next = newSentinel.dataset.next;
              loading = false;
            } else {
              observer.disconnect();
              sentinel.remove();
            }
            syncSoon();
          })
          .catch(function () { loading = false; });
      }, { root: track, rootMargin: '0px 800px 0px 0px' });

      observer.observe(sentinel);
    }

    /* Fonts and lazy images change the track's width after first layout, so the arrow
       state is re-derived whenever the track resizes rather than once here.

       A width change also moves the loop's two landmarks, because the cards are sized
       as a fraction of the viewport. They are re-read from live layout and the position
       is re-homed proportionally — NOT rebuilt. Rebuilding would reset the scroll to the
       start, and on a phone `resize` fires every time the address bar collapses, which
       would throw the row back to card 1 on the first swipe. The buffer is only
       re-grown, never re-cut, so a window that got wider stays covered. */
    var lastW = track.clientWidth;
    new ResizeObserver(function () {
      var w = track.clientWidth;
      if (loop && w !== lastW) {
        // How far through the set the row was, as a fraction of the set, applied to
        // the re-measured landmarks. Approximate by nature — the sample is the last
        // scroll before the resize — but it keeps the row roughly where the reader
        // left it instead of throwing it back to card 1, and it keeps the position
        // inside the valid window so the loop stays coherent at the new width.
        var at = loop.frac || 0;
        loop.grow();
        loop.measure();
        holdUntil = 0;
        jumpTo(loop.home + at * loop.period);
      }
      lastW = w;
      syncSoon();
    }).observe(track);

    sync();
  }

  measureBleed();
  wraps.forEach(init);

  // Two row sections = this file executed twice, but the resize listener is global
  // and only needs to exist once.
  if (!window.__jjrResizeBound) {
    window.__jjrResizeBound = true;
    window.addEventListener('resize', measureBleed, { passive: true });
  }
})();
